import type { GiftDependent } from 'pb-billing-ui/gift-dependents.ts';
import type { License } from '~/types/person/person';

/**
 * Free licenses: a member on Pro or Signature earns one Essentials license a month to give away,
 * by an emailed invitation or a share link. Whoever accepts is covered for as long as the giver
 * stays subscribed. Every shape here is one app-api's `/v3/me/license-gifts` and
 * `/v3/public/license-gifts/{token}` routes answer with.
 */

/** An emailed invitation still waiting to be accepted. It holds one of the giver's licenses until then. */
export interface LicenseGiftInvitation {
    id: number;
    /** The first name the giver typed. */
    first_name: string;
    /** The address the invitation went to. */
    email: string;
    /** When it was last sent, ISO-8601. A resend moves it, and moves `expires_at` on with it. */
    sent_at: string;
    /** When the invitation stops working and its license comes back, ISO-8601. */
    expires_at: string;
}

/**
 * Someone who accepted one of the giver's licenses, whatever became of it since: app-api says no
 * more, so nothing here says whether the giver's plan still protects them (`dependents` does). A
 * snapshot taken when they accepted, linking to nothing: the dependent's row, keyed by the gift.
 */
export type LicenseGiftRecipient = GiftDependent & { id: number };

/** The giver's share link: anyone who opens it can accept one of their licenses until they run out. */
export interface LicenseGiftShareLink {
    url: string;
    /** ISO-8601. */
    expires_at: string;
}

/** Whose plan extends the member's own seat. */
export interface LicenseGiftSponsor {
    /** Null only where the giver's account no longer names one. */
    first_name: string | null;
    /**
     * Set once the giver's plan has ended: the calendar day the extended seat ends, `YYYY-MM-DD`,
     * unless the giver resumes or the member takes a plan of their own. Null while the giver is
     * subscribed.
     */
    grace_ends_at: string | null;
}

/** `GET /v3/me/license-gifts`: everything Free licenses shows the signed-in member. */
export interface LicenseGiftsOverview {
    /** Whether the member sees Free licenses in their navigation. */
    visible: boolean;
    /** Whether the member's plan earns licenses to give. */
    eligible: boolean;
    /** Licenses the member can give now: 0 while they aren't `eligible`. */
    credits: number;
    /**
     * The calendar day the next license lands, `YYYY-MM-DD`. Null while the member earns none,
     * and on the first day they do, before the day's accrual has scheduled it.
     */
    next_credit_at: string | null;
    /** Null while the member's share link is off or expired, and while they aren't `eligible`. */
    share_link: LicenseGiftShareLink | null;
    /** Invitations still waiting to be accepted, none of them expired, the latest sent first. */
    invitations: LicenseGiftInvitation[];
    /** Every gift of the member's that someone accepted, whatever became of it since, the first to accept first. */
    recipients: LicenseGiftRecipient[];
    /**
     * The people the member's plan protects now, the first to accept first: the ones the screens
     * that end the plan name. The shared package's row, so hive and checkout read one definition.
     */
    dependents: GiftDependent[];
    /** How many people the member's plan protects now: as many as `dependents` lists. */
    dependents_count: number;
    /**
     * Set once the member's own plan has ended while it protects people: the calendar day,
     * `YYYY-MM-DD`, their protection ends, unless the member takes Pro or Signature again. Null
     * while the plan runs (a cancellation only scheduled included), and while it protects no one.
     */
    sponsorship_ends_at: string | null;
    /** Null unless another member's plan extends the member's own seat. */
    sponsored_by: LicenseGiftSponsor | null;
}

/** What `POST /v3/me/license-gifts/invitations` takes. */
export interface LicenseGiftInvitationInput {
    first_name: string;
    email: string;
}

/** How a gift reached its recipient: an emailed invitation, or the giver's share link. */
export type LicenseGiftKind = 'invite' | 'link';

/**
 * Why a gift can't be accepted now, as the public read's `reason` and a refused claim's
 * `unavailable_reason` name it. A share link its giver turned off reads as expired, and `revoked` is
 * an invitation Privacy Bee staff withdrew: a member can't withdraw one.
 */
export const GIFT_UNAVAILABLE_REASONS = ['expired', 'claimed', 'revoked', 'exhausted', 'paused'] as const;
export type LicenseGiftUnavailableReason = (typeof GIFT_UNAVAILABLE_REASONS)[number];

/** `GET /v3/public/license-gifts/{token}`: what the claim page may say about a gift before anyone signs in. */
export interface PublicLicenseGift {
    /** Null where the giver's account is gone, which leaves the gift unavailable. */
    sponsor_first_name: string | null;
    kind: LicenseGiftKind;
    available: boolean;
    /** Null while the gift is available. */
    reason: LicenseGiftUnavailableReason | null;
    /** ISO-8601; null where the gift has no expiry to show. */
    expires_at: string | null;
}

/** `POST /v3/me/license-gifts/claim`: the seat the claim gave the member, and whose plan extends it. */
export interface LicenseGiftClaim {
    seat: License;
    sponsor_first_name: string | null;
}
